import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { 
  AlertTriangle, 
  Clock, 
  CheckCircle, 
  Calendar,
  DollarSign,
  ExternalLink
} from 'lucide-react';
import { format, isAfter, isBefore, startOfDay, addDays, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

const PaymentAlertsModal = ({ isOpen, onClose, fixedExpenses = [] }) => {
  const [alerts, setAlerts] = useState([]);
  const navigate = useNavigate();

  // Função para calcular próximo vencimento
  const getNextDueDate = (dueDay) => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    
    // Tenta criar data no mês atual
    let nextDue = new Date(currentYear, currentMonth, dueDay);
    
    // Se já passou, vai para o próximo mês
    if (isBefore(nextDue, startOfDay(today))) {
      nextDue = new Date(currentYear, currentMonth + 1, dueDay);
    }
    
    return nextDue;
  };

  // Função para verificar se foi pago no mês atual
  const isPaidThisMonth = (expense) => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    return expense.paymentHistory?.some(payment => {
      const paymentDate = new Date(payment.paidDate);
      return paymentDate.getMonth() === currentMonth && 
             paymentDate.getFullYear() === currentYear;
    });
  };

  // Calcular alertas
  useEffect(() => {
    if (!fixedExpenses.length) {
      setAlerts([]);
      return;
    }

    const today = startOfDay(new Date());
    const newAlerts = [];
    
    fixedExpenses.forEach(expense => {
      // Pular se já foi pago este mês
      if (isPaidThisMonth(expense)) return;
      
      const nextDue = getNextDueDate(expense.dueDay);
      const daysUntilDue = differenceInDays(nextDue, today);
      const alertDays = expense.alertDaysBefore || 3;
      
      let alertType = null;
      let priority = 0;
      
      if (daysUntilDue < 0) {
        // Vencido
        alertType = 'overdue';
        priority = 3;
      } else if (daysUntilDue === 0) {
        // Vence hoje
        alertType = 'due_today';
        priority = 2;
      } else if (daysUntilDue <= alertDays) {
        // Próximo ao vencimento
        alertType = 'due_soon';
        priority = 1;
      }
      
      if (alertType) {
        newAlerts.push({
          id: `${expense._id}-${format(nextDue, 'yyyy-MM')}`,
          expense,
          type: alertType,
          priority,
          dueDate: nextDue,
          daysUntilDue
        });
      }
    });
    
    // Ordenar por prioridade (maior primeiro) e depois por data
    newAlerts.sort((a, b) => {
      if (a.priority !== b.priority) {
        return b.priority - a.priority;
      }
      return a.dueDate - b.dueDate;
    });
    
    setAlerts(newAlerts);
  }, [fixedExpenses]);

  const getAlertIcon = (type) => {
    switch (type) {
      case 'overdue':
        return <AlertTriangle size={20} color="var(--error-color)" />;
      case 'due_today':
        return <Clock size={20} color="#f97316" />;
      case 'due_soon':
        return <Calendar size={20} color="var(--warning-color)" />;
      default:
        return <Clock size={20} color="var(--text-muted)" />;
    }
  };

  const getAlertMessage = (alert) => {
    const { type, daysUntilDue } = alert;
    
    switch (type) {
      case 'overdue':
        const overdueDays = Math.abs(daysUntilDue);
        return `Vencido há ${overdueDays} dia${overdueDays > 1 ? 's' : ''}`;
      case 'due_today':
        return 'Vence hoje';
      case 'due_soon':
        return `Vence em ${daysUntilDue} dia${daysUntilDue > 1 ? 's' : ''}`;
      default:
        return '';
    }
  };

  const getAlertBorderColor = (type) => {
    switch (type) {
      case 'overdue':
        return 'var(--error-color)';
      case 'due_today':
        return '#f97316';
      case 'due_soon':
        return 'var(--warning-color)';
      default:
        return 'var(--text-muted)';
    }
  };

  const getAlertBackgroundColor = (type) => {
    switch (type) {
      case 'overdue':
        return '#fef2f2';
      case 'due_today':
        return '#fff7ed';
      case 'due_soon':
        return '#fefce8';
      default:
        return '#f9fafb';
    }
  };

  const handleGoToFinancial = () => {
    onClose();
    navigate('/financial?tab=despesasFixas');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Alertas de Pagamento"
      size="lg"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {alerts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <CheckCircle 
              size={48} 
              color="var(--success-color)" 
              style={{ margin: '0 auto 1rem' }} 
            />
            <h3 style={{ 
              fontSize: '1.125rem', 
              fontWeight: '500', 
              color: 'var(--text-primary)', 
              marginBottom: '0.5rem' 
            }}>
              Nenhum alerta pendente
            </h3>
            <p style={{ color: 'var(--text-secondary)' }}>
              Todas as despesas fixas estão em dia!
            </p>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: '1rem' }}>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                {alerts.length} alerta{alerts.length > 1 ? 's' : ''} pendente{alerts.length > 1 ? 's' : ''}
              </p>
            </div>
            
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '0.75rem', 
              maxHeight: '24rem', 
              overflowY: 'auto' 
            }}>
              {alerts.map((alert) => (
                <div
                  key={alert.id}
                  className="card"
                  style={{
                    borderLeft: `4px solid ${getAlertBorderColor(alert.type)}`,
                    backgroundColor: getAlertBackgroundColor(alert.type),
                    padding: '1rem'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                      {getAlertIcon(alert.type)}
                      <div style={{ flex: 1 }}>
                        <h4 style={{ 
                          fontWeight: '500', 
                          color: 'var(--text-primary)', 
                          marginBottom: '0.25rem' 
                        }}>
                          {alert.expense.name}
                        </h4>
                        <p style={{ 
                          fontSize: '0.875rem', 
                          color: 'var(--text-secondary)', 
                          marginBottom: '0.5rem' 
                        }}>
                          {getAlertMessage(alert)}
                        </p>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '1rem', 
                          fontSize: '0.75rem', 
                          color: 'var(--text-muted)' 
                        }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <Calendar size={12} />
                            Vencimento: {format(alert.dueDate, 'dd/MM/yyyy', { locale: ptBR })}
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <DollarSign size={12} />
                            R$ {alert.expense.amount?.toFixed(2) || '0,00'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          paddingTop: '1rem', 
          borderTop: '1px solid var(--border-color)' 
        }}>
          <button
            onClick={onClose}
            className="btn btn-outline"
            style={{ marginRight: '0.5rem' }}
          >
            Fechar
          </button>
          <button
            onClick={handleGoToFinancial}
            className="btn btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <span>Ir para Financeiro</span>
            <ExternalLink size={16} />
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PaymentAlertsModal;